/**
 * Roadmap-matrix — berekend render-pad (layout_mode = "roadmap-matrix",
 * roadmap-tijdreizen.md §6.4). Eén template, bij aanmaken gebonden aan één
 * Program (= Roadmap → één toekomstpad) en één concept-type/domein. Kolommen =
 * de plateaus van dat Program; rijen = de elementen die de modelleur erop sleept.
 * Cel = het element zoals het op die plateau-branch bestaat + het
 * «ConceptState»-stereotype met een state_name.
 *
 * CHUNK B — read-only render in roadmap-stijl: gekleurde plateau-kolommen
 * (zelfde kleur als de roadmap-lanes, uit het Plateau-object) + element-rijen
 * uit de config. De drag-to-matrix en de per-plateau-branch cel-versies +
 * «ConceptState»-stateName + flow-pijlen volgen in chunk C.
 */

import type { BilingualText, BoxStyle, Concept, Diagram } from "@ae/domain";
import {
  type DragEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { api, type PlateauBranch } from "../../api/client";
import { GlossaryInfoButton } from "../../help/uiGlossary";
import { getRegistry, resolveBilingual } from "../../registry";
import { useDisplayMode } from "../../state/displayMode";
import { useSession } from "../../state/session";
import { useWorkspaceData } from "../../state/workspace";
import { colorForConcept, type LayerColor } from "../layerColors";
import { BoxFormatToolbar } from "./BoxFormatToolbar";
import { boxFillCss, boxTextCss, readBoxStyles } from "./diagramBoxStyles";
import { getElementIcon } from "./elementIcons";
import { readRoadmapMatrixConfig } from "./roadmapMatrixConfig";
import { ZoomPan } from "./ZoomPan";

interface RoadmapMatrixCanvasProps {
  diagram: Diagram;
}

// Drag-MIMEs (zelfde als Explorer/RoadmapCanvas).
const EXISTING_CONCEPT_MIME = "application/ae-existing-concept-id";
const EXISTING_CONCEPT_IDS_MIME = "application/ae-existing-concept-ids";

/** Is dit concept toegestaan op een matrix met dit concept-type/domein-filter?
 *  Domein-keys uit MATRIX_DOMAINS: bmc / sharp / c4 / archimate / risk. */
function isEligibleForMatrix(concept: Concept, filter: string): boolean {
  const stereos = concept.stereotypes ?? [];
  const has = (prefix: string) => stereos.some((s) => s.startsWith(prefix));
  switch (filter) {
    case "bmc":
      return has("bmc.");
    case "sharp":
      return has("sharp.") || concept.typeId.startsWith("sharp.");
    case "c4":
      return has("c4.");
    case "risk":
      return has("risk.");
    case "archimate":
      return concept.typeId.startsWith("archimate.");
    default:
      return false;
  }
}

/** Parse de bulk-drag-payload (JSON-array van concept-ids) defensief. */
function parseBulkIds(raw: string): string[] {
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** Resolveert een stereotype-id naar z'n weergavelabel mét «»-guillemets.
 *  Sharp-labels dragen de guillemets al; andere (bv. roadmap.ConceptState) niet
 *  → dan wikkelen we 'm zodat de ArchiMate-stereotype-notatie consistent is. */
function stereotypeLabel(stereoId: string, lang: "nl" | "en"): string | null {
  const s = getRegistry().getStereotype(stereoId);
  if (!s) return null;
  const raw = resolveBilingual(s.label, lang).trim();
  if (!raw) return null;
  return raw.startsWith("«") ? raw : `«${raw}»`;
}

/** Kaartje voor een element-rij in een plateau-lane — afgeronde rechthoek met
 *  type-icoon + (stereotype boven) naam, in de eigen object-kleur. */
function ElementCard({
  concept,
  lang,
  onRemove,
  rowKey,
  boxStyle,
  selected,
  onSelect,
}: {
  concept: Concept;
  lang: "nl" | "en";
  onRemove?: () => void;
  rowKey?: string;
  boxStyle?: BoxStyle | undefined;
  selected?: boolean | undefined;
  onSelect?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  const et = reg.getElementType(concept.typeId);
  const displayMode = useDisplayMode((s) => s.mode);
  const color = colorForConcept({
    typeId: concept.typeId,
    layer: et?.layer ?? "business",
    attributes: concept.attributes,
    stereotypes: concept.stereotypes,
    displayMode,
  });
  const name = resolveBilingual(concept.name, lang) || t("roadmapMatrix.unnamed");
  // Eerste stereotype dat naar een label resolvet (bv. «Concept»).
  const stereo = (concept.stereotypes ?? [])
    .map((id) => stereotypeLabel(id, lang))
    .find((l): l is string => Boolean(l));
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
    <div
      title={name}
      data-matrix-row={rowKey}
      onClick={onSelect}
      className={`group relative flex flex-col items-center justify-center rounded-lg border px-3 py-2 text-center text-xs shadow-sm ${
        selected ? "ring-2 ring-blue-500" : ""
      }`}
      style={{
        backgroundColor: color.bg,
        borderColor: color.border,
        color: color.text,
        ...boxFillCss(boxStyle),
        ...boxTextCss(boxStyle),
      }}
    >
      {/* Type-icoon rechtsboven, zoals op het normale canvas. */}
      <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center opacity-80">
        {getElementIcon(concept.typeId)}
      </span>
      {stereo && <span className="break-words text-[9px] leading-tight opacity-70">{stereo}</span>}
      <span className="break-words font-medium">{name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          title={t("roadmapMatrix.removeRow")}
          className="absolute top-1 left-1 rounded px-1 text-[11px] leading-none opacity-0 hover:bg-black/10 group-hover:opacity-70"
        >
          ✕
        </button>
      )}
    </div>
  );
}

/** Cel in een plateau-lane: de «ConceptState»-stateName voor één element op één
 *  plateau. **Meertalig** — toont de actieve UI-taal; op blur committen + de
 *  andere taal auto-vertalen via Claude (last-edit-wins, net als BilingualField).
 *  412/geen-key → silent fallback: kopieert de bron naar de andere taal zodat de
 *  cel niet leeg blijft. Lokale state + onBlur-persist zodat niet elke toets
 *  een config-write triggert. */
function StateCell({
  value,
  lang,
  borderColor,
  placeholder,
  rowKey,
  boxStyle,
  selected,
  onSelect,
  onCommit,
}: {
  value: BilingualText;
  lang: "nl" | "en";
  borderColor?: string | undefined;
  placeholder: string;
  rowKey?: string;
  boxStyle?: BoxStyle | undefined;
  selected?: boolean | undefined;
  onSelect?: (() => void) | undefined;
  onCommit: (next: BilingualText) => void;
}) {
  const { t } = useTranslation();
  // Alleen kijken: de toestand is te lezen, niet te typen.
  const readOnly = useSession((s) => s.readOnly);
  const shown = value[lang] ?? "";
  const [local, setLocal] = useState(shown);
  const [translating, setTranslating] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setLocal(shown);
  }, [shown]);
  // Auto-grow: laat het tekstveld meegroeien met de inhoud (word-wrap) i.p.v.
  // afkappen. Hoogte resetten naar scrollHeight bij elke waarde-wijziging.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [local]);

  const commit = async () => {
    const trimmed = local.trim();
    if (trimmed === (value[lang] ?? "")) return;
    if (useSession.getState().readOnly) return;
    // Lege invoer → wis deze taal; geen vertaling.
    if (!trimmed) {
      const cleared: BilingualText = { ...value };
      delete cleared[lang];
      onCommit(cleared);
      return;
    }
    setTranslating(true);
    try {
      // Auto-detect de taal van de getypte tekst → de input belandt in z'n
      // eigen taalveld en de vertaling in het andere, ongeacht de actieve
      // UI-taal (voorkomt nl/en-omwisseling bij typen in een andere UI-taal).
      const res = await api.translateAuto(trimmed, "name");
      const src: "nl" | "en" = res.source_lang;
      const other: "nl" | "en" = src === "nl" ? "en" : "nl";
      onCommit({ ...value, [src]: trimmed, [other]: res.text });
    } catch {
      // Geen vertaling beschikbaar (geen key/fout) → bewaar tenminste de input
      // in het actieve taalveld zodat 't niet verloren gaat.
      onCommit({ ...value, [lang]: trimmed });
    } finally {
      setTranslating(false);
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
    <div
      data-matrix-row={rowKey}
      onClick={onSelect}
      className={`relative flex flex-col rounded-lg border bg-white/80 px-2 py-1.5 shadow-sm ${
        selected ? "ring-2 ring-blue-500" : ""
      }`}
      style={{ ...(borderColor ? { borderColor } : {}), ...boxFillCss(boxStyle) }}
    >
      <div className="text-center text-[9px] text-slate-400 leading-tight">
        {stereotypeLabel("roadmap.ConceptState", lang) ?? "«ConceptState»"}
      </div>
      <textarea
        ref={taRef}
        value={local}
        placeholder={placeholder}
        rows={1}
        readOnly={readOnly}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => void commit()}
        style={boxTextCss(boxStyle)}
        className="block w-full resize-none overflow-hidden break-words bg-transparent text-center text-slate-700 text-xs outline-none placeholder:text-slate-400"
      />
      {translating && (
        <span className="-translate-y-1/2 absolute top-1/2 right-1.5 text-[9px] text-blue-600">
          {t("roadmapMatrix.translating")}
        </span>
      )}
    </div>
  );
}

export function RoadmapMatrixCanvas({ diagram }: RoadmapMatrixCanvasProps) {
  const { t, i18n } = useTranslation();
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: geen rijen erbij of eraf, geen toestand, geen opmaak.
  const readOnly = useSession((s) => s.readOnly);
  const revision = useWorkspaceData((s) => s.revision);
  const displayMode = useDisplayMode((s) => s.mode);

  // `revision` in de deps: setRoadmapMatrixConfig muteert diagram.viewState
  // in-place (zelfde object-ref), dus zonder revision zou de memo nooit
  // herberekenen na een config-/rij-wijziging.
  const config = useMemo(() => {
    void revision;
    return readRoadmapMatrixConfig(diagram.viewState);
  }, [diagram.viewState, revision]);
  const configured = Boolean(config.programId && config.conceptFilter);

  const [plateaus, setPlateaus] = useState<PlateauBranch[]>([]);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [dragOver, setDragOver] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Per-box opmaak (font/kleur/uitlijning). `selectedBoxKey` = de box waarvan de
  // opmaak-balk getoond wordt; styles staan in de viewState-stash.
  const [selectedBoxKey, setSelectedBoxKey] = useState<string | null>(null);
  const boxStyles = useMemo(() => {
    void revision;
    return readBoxStyles(diagram.viewState);
  }, [diagram.viewState, revision]);
  const patchBox = useCallback(
    (key: string, patch: BoxStyle) => {
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      useWorkspaceData.getState().setDiagramBoxStyle(workspaceId, branchId, diagram.id, key, patch);
    },
    [workspaceId, branchId, diagram.id],
  );
  // Cross-branch concept-resolutie. De matrix is branch-transparant: een rij
  // moet zichtbaar zijn op ELKE actieve branch zodra 't object op één
  // plateau-branch leeft, terwijl de actieve-branch-store alleen z'n eigen
  // overlay kent. We halen per plateau-branch de concept-overlay op en bouwen
  // (a) `union` (id→Concept) voor de rij-resolutie in de linkerkolom en
  // (b) `presence` (plateau-branch-id → set van levende concept-ids) zodat een
  // cel in een plateau-lane alleen verschijnt als 't object in DAT plateau leeft.
  // Gekeyd op LOGISCHE identiteit (`originId ?? id`) zodat een element over
  // plateau-branches herkenbaar blijft, ook nadat een state-edit een
  // branch-kopie met een nieuw id maakte. `union` = id→Concept (rij-resolutie);
  // `byPlateau` = plateau-branch-id → (logische-id → het concept op DIE branch),
  // bron voor de cel-aanwezigheid + de «ConceptState».state_name-waarde.
  const [crossBranch, setCrossBranch] = useState<{
    union: Map<string, Concept>;
    byPlateau: Record<string, Map<string, Concept>>;
  }>({ union: new Map(), byPlateau: {} });
  // Bump na een state-write zodat de per-plateau-overlay opnieuw geladen wordt
  // (de copy-on-write geeft het element op die branch een nieuw id).
  const [refreshToken, setRefreshToken] = useState(0);

  // Plateau-keten = de kolommen. Chunk 2 leest de keten van de huidige WP
  // (zoals RoadmapCanvas); de Program-specifieke resolutie volgt in chunk 3.
  useEffect(() => {
    if (!configured || !workspaceId || !branchId) {
      setState("idle");
      return;
    }
    let cancelled = false;
    setState("loading");
    api
      .branchPlateaus(workspaceId, branchId)
      .then((res) => {
        if (cancelled) return;
        setPlateaus(res);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [configured, workspaceId, branchId]);

  // Haal per plateau-branch de concept-overlay op → unie-map + per-plateau-
  // presence. `revision` in de deps zodat een drag/edit op de actieve branch de
  // cross-branch-set ververst (anders zou een vers gesleept object pas na een
  // branch-wissel in de andere lanes verschijnen).
  useEffect(() => {
    if (!workspaceId || plateaus.length === 0) {
      setCrossBranch({ union: new Map(), byPlateau: {} });
      return;
    }
    void revision;
    void refreshToken;
    let cancelled = false;
    Promise.all(
      plateaus.map((p) =>
        api
          .listConcepts(workspaceId, p.id)
          .then((res) => ({ branchId: p.id, concepts: res.items }))
          .catch(() => ({ branchId: p.id, concepts: [] as Concept[] })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const union = new Map<string, Concept>();
      const byPlateau: Record<string, Map<string, Concept>> = {};
      for (const { branchId: bid, concepts } of results) {
        const m = new Map<string, Concept>();
        for (const c of concepts) {
          const lid = c.originId ?? c.id;
          m.set(lid, c);
          if (!union.has(lid)) union.set(lid, c);
        }
        byPlateau[bid] = m;
      }
      setCrossBranch({ union, byPlateau });
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, plateaus, revision, refreshToken]);

  const plateauLabel = useCallback(
    (p: PlateauBranch) => {
      // Toon de naam van het gekoppelde Plateau-concept (niet de branch-naam).
      if (p.plateauConceptId) {
        const store = useWorkspaceData.getState().store;
        const c = store.getConcept(p.plateauConceptId) ?? crossBranch.union.get(p.plateauConceptId);
        const nm = c ? resolveBilingual(c.name, lang) : "";
        if (nm) return nm;
      }
      return p.isMain ? t("roadmapMatrix.now") : p.name;
    },
    [t, lang, crossBranch.union],
  );

  // Rij-elementen uit de config (zaken-van-belang). `revision` triggert
  // her-lezen na een store-mutatie.
  const rows = useMemo<Concept[]>(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const ids = config.rowConceptIds ?? [];
    // Resolve cross-branch: een rij-concept dat alleen op een andere plateau-
    // branch leeft zit niet in de actieve-branch-store. De unie-map dekt alle
    // plateau-branches; store is fallback voor vers gesleepte rijen.
    return ids
      .map((id) => crossBranch.union.get(id) ?? store.getConcept(id))
      .filter((c): c is Concept => Boolean(c));
  }, [config.rowConceptIds, revision, crossBranch.union]);

  const persistRows = useCallback(
    (ids: string[]) => {
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      useWorkspaceData.getState().setRoadmapMatrixConfig(workspaceId, branchId, diagram.id, {
        ...config,
        rowConceptIds: ids,
      });
    },
    [workspaceId, branchId, diagram.id, config],
  );

  const removeRow = useCallback(
    (conceptId: string) => {
      persistRows((config.rowConceptIds ?? []).filter((id) => id !== conceptId));
    },
    [config.rowConceptIds, persistRows],
  );

  // Zet/wis de «ConceptState».state_name als ECHT attribuut op het element op de
  // plateau-branch (zuivere variant). De worker doet copy-on-write zodat de
  // waarde per branch leeft en in het AttributePanel verschijnt. `plateauConcept`
  // = het element zoals het op die branch bestaat (uit `byPlateau`).
  const writeState = useCallback(
    async (plateauBranchId: string, plateauConcept: Concept, value: BilingualText) => {
      if (!workspaceId) return;
      if (useSession.getState().readOnly) return;
      const stereotypes = plateauConcept.stereotypes.includes("roadmap.ConceptState")
        ? plateauConcept.stereotypes
        : [...plateauConcept.stereotypes, "roadmap.ConceptState"];
      const attributes: Record<string, unknown> = { ...plateauConcept.attributes };
      if (value && Object.keys(value).length > 0) attributes.state_name = value;
      else delete attributes.state_name;
      try {
        await api.patchConcept(workspaceId, plateauBranchId, plateauConcept.id, {
          stereotypes,
          attributes,
        });
      } finally {
        setRefreshToken((x) => x + 1);
      }
    },
    [workspaceId],
  );

  // Éénmalige migratie: bestaande toestand-waarden uit de config-stash naar het
  // echte «ConceptState».state_name-attribuut op het element per plateau-branch,
  // en daarna de stash leegmaken. Draait zodra de per-plateau-overlay geladen is.
  const migratedRef = useRef(false);
  useEffect(() => {
    if (migratedRef.current || !workspaceId || !branchId) return;
    // Een lezer migreert niet: dat is schrijven. De eerste schrijver doet het.
    if (useSession.getState().readOnly) return;
    const rs = config.rowStates;
    if (!rs || Object.keys(rs).length === 0) return;
    if (Object.keys(crossBranch.byPlateau).length === 0) return;
    migratedRef.current = true;
    void (async () => {
      for (const [conceptId, perPlateau] of Object.entries(rs)) {
        for (const [plateauId, bilingual] of Object.entries(perPlateau)) {
          const pc = crossBranch.byPlateau[plateauId]?.get(conceptId);
          if (pc && bilingual && Object.keys(bilingual).length > 0) {
            await writeState(plateauId, pc, bilingual);
          }
        }
      }
      useWorkspaceData.getState().setRoadmapMatrixConfig(workspaceId, branchId, diagram.id, {
        ...config,
        rowStates: undefined,
      });
    })();
  }, [config, crossBranch.byPlateau, workspaceId, branchId, diagram.id, writeState]);

  // Drop een element-van-belang in de linkerkolom → als rij toevoegen, mits 't
  // bij het gekozen concept-type/domein past en er nog niet op staat.
  const onDropLeft = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (!config.conceptFilter) return;
      if (useSession.getState().readOnly) return;
      const store = useWorkspaceData.getState().store;
      const bulkRaw = e.dataTransfer.getData(EXISTING_CONCEPT_IDS_MIME) || null;
      const single = e.dataTransfer.getData(EXISTING_CONCEPT_MIME) || null;
      const ids = bulkRaw ? parseBulkIds(bulkRaw) : single ? [single] : [];
      if (ids.length === 0) return;
      const existing = config.rowConceptIds ?? [];
      const seen = new Set(existing);
      const eligible: string[] = [];
      let rejected = 0;
      for (const id of ids) {
        if (seen.has(id)) continue;
        const c = store.getConcept(id);
        if (!c) continue;
        if (isEligibleForMatrix(c, config.conceptFilter)) {
          eligible.push(id);
          seen.add(id);
        } else {
          rejected++;
        }
      }
      if (eligible.length > 0) persistRows([...existing, ...eligible]);
      else if (rejected > 0) setNotice(t("roadmapMatrix.dropTypeMismatch"));
    },
    [config.conceptFilter, config.rowConceptIds, persistRows, t],
  );

  // Kolom-kleur = de kleur van het gekoppelde Plateau-object (zoals de
  // roadmap-lanes), zodat de matrix er visueel hetzelfde uitziet als het
  // Roadmap-diagram. Geen plateau-object (bv. Plateau 1 = main) → default
  // Plateau-kleur.
  const plateauColors = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    const map = new Map<string, LayerColor>();
    for (const p of plateaus) {
      const c = p.plateauConceptId ? store.getConcept(p.plateauConceptId) : null;
      if (c) {
        const et = reg.getElementType(c.typeId);
        map.set(
          p.id,
          colorForConcept({
            typeId: c.typeId,
            layer: et?.layer ?? "implementation_migration",
            attributes: c.attributes,
            stereotypes: c.stereotypes,
            displayMode,
          }),
        );
      } else {
        const et = reg.getElementType("archimate.Plateau");
        map.set(
          p.id,
          colorForConcept({
            typeId: "archimate.Plateau",
            layer: et?.layer ?? "implementation_migration",
          }),
        );
      }
    }
    return map;
  }, [plateaus, revision, displayMode]);

  // Rij-hoogte-sync: cellen die horizontaal op dezelfde rij staan (zelfde
  // concept-id, gemarkeerd met `data-matrix-row`) krijgen allemaal de hoogte van
  // de hoogste cel in die rij — inclusief het ElementCard. Een ResizeObserver
  // vangt content-groei (bv. een vertaling die later landt of word-wrap); een
  // `syncing`-guard + rAF voorkomt een feedback-loop met de min-height-writes.
  const bodyRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    let raf = 0;
    let syncing = false;
    const sync = () => {
      if (syncing) return;
      syncing = true;
      const cells = Array.from(root.querySelectorAll<HTMLElement>("[data-matrix-row]"));
      for (const el of cells) el.style.minHeight = "";
      const maxByRow = new Map<string, number>();
      for (const el of cells) {
        const r = el.dataset.matrixRow ?? "";
        maxByRow.set(r, Math.max(maxByRow.get(r) ?? 0, el.offsetHeight));
      }
      for (const el of cells) {
        const m = maxByRow.get(el.dataset.matrixRow ?? "");
        if (m) el.style.minHeight = `${m}px`;
      }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        syncing = false;
      });
    };
    sync();
    const ro = new ResizeObserver(() => sync());
    for (const el of root.querySelectorAll<HTMLElement>("[data-matrix-row]")) ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [rows, plateaus, revision, lang, state]);

  const title = resolveBilingual(diagram.name, lang) || t("roadmapMatrix.title");

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="absolute top-2 right-2 z-10">
        <GlossaryInfoButton
          termIds={[
            "roadmap",
            "plateau",
            "branch",
            "concept_state",
            "concept",
            "stereotype",
            "gap",
          ]}
        />
      </div>
      <div className="border-slate-200 border-b px-4 py-2">
        <h2 className="text-center font-semibold text-slate-700 text-sm">{title}</h2>
        <p className="mt-0.5 text-center text-slate-500 text-xs">{t("roadmapMatrix.subtitle")}</p>
      </div>

      {/* Nog niet geconfigureerd → prompt (config-bar zelf komt in chunk 3). */}
      {!configured && (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-slate-500 text-sm">
          <p className="font-medium">{t("roadmapMatrix.notConfiguredTitle")}</p>
          <p className="max-w-md text-slate-400 text-xs">{t("roadmapMatrix.notConfiguredHint")}</p>
        </div>
      )}

      {configured && state === "loading" && (
        <div className="flex flex-1 items-center justify-center text-slate-500 text-sm">
          {t("roadmapMatrix.loading")}
        </div>
      )}
      {configured && state === "error" && (
        <div className="flex flex-1 items-center justify-center text-red-600 text-sm">
          {t("roadmapMatrix.error")}
        </div>
      )}

      {/* Lanes-per-plateau — exact dezelfde layout als het Roadmap-diagram. Elke
          lane = een plateau (gekleurd uit het Plateau-object); de body lijst de
          elementen-van-belang als kaartjes (dezelfde set + volgorde per lane,
          dus ze lijnen horizontaal uit). Drag-to-lane volgt in chunk C. */}
      {configured && state === "ready" && (
        <ZoomPan className="min-h-0 flex-1" persistKey={diagram.id}>
          <div ref={bodyRef} className="flex gap-3 p-4">
            {/* Linkerkolom = drop-zone: sleep hier de elementen-van-belang naartoe.
              Ze verschijnen daarna als rij in elke plateau-lane. (De drag zelf
              komt in chunk C.) */}
            <div className="flex w-56 shrink-0 flex-col rounded-lg border border-slate-300">
              <div className="rounded-t-lg bg-slate-100 px-3 py-2">
                <span className="font-medium text-slate-700 text-sm">
                  {t("roadmapMatrix.elementColumn")}
                </span>
              </div>
              {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "copy";
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDropLeft}
                className={`flex min-h-[80px] flex-1 flex-col gap-1.5 rounded-b-lg bg-white p-2 ${
                  dragOver ? "ring-2 ring-blue-400 ring-inset" : ""
                }`}
              >
                {rows.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center px-2 text-center text-slate-400 text-xs">
                    {t("roadmapMatrix.dropElementHint")}
                  </div>
                ) : (
                  rows.map((c) => (
                    <ElementCard
                      key={c.id}
                      concept={c}
                      lang={lang}
                      rowKey={c.id}
                      boxStyle={boxStyles[`el:${c.id}`]}
                      selected={selectedBoxKey === `el:${c.id}`}
                      onSelect={() => setSelectedBoxKey(`el:${c.id}`)}
                      {...(readOnly ? {} : { onRemove: () => removeRow(c.id) })}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Plateau-lanes in roadmap-stijl. Geen drop-hint hier — droppen loopt
              via de linkerkolom; deze lanes spiegelen de rijen per plateau. */}
            {plateaus.map((p, idx) => {
              const color = plateauColors.get(p.id);
              const isActive = p.id === branchId;
              return (
                <div
                  key={p.id}
                  className={`flex w-64 shrink-0 flex-col rounded-lg border ${
                    isActive ? "ring-2 ring-blue-400" : ""
                  }`}
                  style={color ? { borderColor: color.border } : undefined}
                >
                  <div
                    className="flex items-center justify-between gap-2 rounded-t-lg px-3 py-2"
                    style={
                      color
                        ? {
                            backgroundColor: color.bg,
                            backgroundImage: "linear-gradient(rgba(0,0,0,0.09), rgba(0,0,0,0.09))",
                            color: color.text,
                          }
                        : undefined
                    }
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="font-medium text-slate-800 text-sm"
                        style={color ? { color: color.text } : undefined}
                      >
                        {plateauLabel(p)}
                      </span>
                      {idx === 0 && (
                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">
                          {t("roadmapMatrix.now")}
                        </span>
                      )}
                    </span>
                    {isActive && (
                      <span className="rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] text-white">
                        {t("roadmapMatrix.active")}
                      </span>
                    )}
                  </div>
                  <div
                    className="flex min-h-[80px] flex-1 flex-col gap-1.5 rounded-b-lg p-2"
                    style={color ? { backgroundColor: color.bg } : undefined}
                  >
                    {rows.map((c) => {
                      // Het element zoals het op DEZE plateau-branch bestaat
                      // (logische-id-match). Cel alleen tonen als 't er leeft;
                      // anders een lege placeholder voor de uitlijning. De
                      // toestand = de «ConceptState».state_name op dat element.
                      const pc = crossBranch.byPlateau[p.id]?.get(c.id);
                      return pc ? (
                        <StateCell
                          key={c.id}
                          value={(pc.attributes?.state_name as BilingualText) ?? {}}
                          lang={lang}
                          borderColor={color?.border}
                          placeholder={t("roadmapMatrix.statePlaceholder")}
                          rowKey={c.id}
                          boxStyle={boxStyles[`cell:${c.id}:${p.id}`]}
                          selected={selectedBoxKey === `cell:${c.id}:${p.id}`}
                          onSelect={() => setSelectedBoxKey(`cell:${c.id}:${p.id}`)}
                          onCommit={(next) => void writeState(p.id, pc, next)}
                        />
                      ) : (
                        <div
                          key={c.id}
                          data-matrix-row={c.id}
                          className="rounded-lg border border-slate-200 border-dashed bg-slate-50/40 px-2 py-1.5"
                          title={t("roadmapMatrix.notInPlateau")}
                        >
                          <div className="text-center text-[10px] text-slate-300 leading-4">–</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ZoomPan>
      )}

      {selectedBoxKey && !readOnly && (
        <BoxFormatToolbar
          style={boxStyles[selectedBoxKey] ?? {}}
          onChange={(p) => patchBox(selectedBoxKey, p)}
          onClose={() => setSelectedBoxKey(null)}
        />
      )}

      {notice && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30">
          <div className="w-[340px] rounded-lg border border-slate-200 bg-white p-4 shadow-xl">
            <div className="mb-3 text-slate-700 text-sm">{notice}</div>
            <div className="flex justify-end">
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                onClick={() => setNotice(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
